import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Skeleton } from "@mui/material";
import { endOfDay, isWithinInterval, startOfDay } from "date-fns";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import ScrollableWrapper from "./ScrollableWrapper";
import ColumnFilterMenu, {
  ColumnDateFilterMenu,
  ColumnFilterOption,
} from "./ColumnFilterMenu";
import ordersTableStyles from "../orders/ordersTableStyles";
import { StatusTone } from "../../utils/labPanelStatus";
import {
  clearRecordFilters,
  readRecordFilters,
  writeRecordFilters,
} from "./recordTableFilterStore";

/**
 * The platform's record listing: a card-per-row table whose filters live in the
 * column headers.
 *
 * One component for History's three listings (Assessments, Programming,
 * Prescription) so they cannot drift — they were three hand-rolled tables with
 * three copies of the filter plumbing. Columns are declared, not laid out: each
 * one says how to read its value, how to filter it and (optionally) how to
 * render it, and this component derives the filter options from the rows it was
 * given.
 *
 * Two deliberate choices:
 *
 * - **The "Clear Filters" pill is portalled**, not rendered above the table.
 *   Showing it inline pushed every row down the moment a filter was applied;
 *   the host passes a slot on a line that already exists (History puts it on
 *   the tab strip) so the table never moves. With no slot it falls back to an
 *   inline row.
 * - **Filtering is client-side over the rows passed in.** Every listing here
 *   either has no server-side filters or has ones that can't see all its rows
 *   (Assessments merges two services), so the filter options are always exactly
 *   the values present.
 * - **Rendering is incremental.** The filters need every row in memory, but
 *   the DOM does not: a company's service history is thousands of rows, and
 *   mounting them all blocked the page for tens of seconds. The table renders
 *   the first `RENDER_CHUNK` filtered rows and appends another chunk whenever
 *   the user scrolls past `RENDER_MORE_THRESHOLD` of the list (or the list is
 *   too short to scroll), with a "Showing x of y" caption at the end.
 */

/** Rows mounted at once; another chunk is appended on scroll. */
const RENDER_CHUNK = 100;
/** Fraction of the scroll height past which the next chunk is appended. */
const RENDER_MORE_THRESHOLD = 0.7;

export type RecordColumnFilterType = "multi" | "date" | "none";

export interface RecordColumn<T> {
  key: string;
  label: string;
  /** Flex-grow for the cell, as in the other platform tables. */
  flex: number;
  /** Defaults to "multi"; "none" renders a plain header label. */
  filter?: RecordColumnFilterType;
  /** Adds a search field to a "multi" filter — for long lists like clients. */
  searchable?: boolean;
  /** Show initials avatars in a "multi" filter's option list. */
  withInitials?: boolean;
  /** The cell's text, and a "multi" filter's default option key and label. */
  value: (row: T) => string | null | undefined;
  /** Option key, when it should differ from `value` (e.g. a client id). */
  optionKey?: (row: T) => string | null | undefined;
  /**
   * Multi-valued columns — a prescription's Services cell lists several. The
   * filter offers the union across rows, and a row matches when ANY of its
   * values is selected. Overrides `value`/`optionKey` for filtering.
   */
  optionsOf?: (row: T) => { key: string; label: string }[];
  /** The date a "date" filter compares against. */
  date?: (row: T) => string | Date | null | undefined;
  /** Custom cell content — a badge, a truncated list. Falls back to `value`. */
  render?: (row: T) => React.ReactNode;
  /**
   * How to wrap `render`'s output. "node" (the default) is a bare flex cell,
   * for badges that bring their own type; "text" applies the table's cell
   * typography and truncation, for rich text like a truncated service list.
   */
  cellStyle?: "node" | "text";
}

interface RecordTableProps<T> {
  columns: RecordColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  /** Colored left accent for rows needing attention. */
  accent?: (row: T) => string | null;
  /** Renders the sliding "Review" tile on hover for rows needing attention. */
  reviewTone?: (row: T) => StatusTone | null;
  emptyMessage?: string;
  filteredEmptyMessage?: string;
  /** Banners (errors, notices) rendered above the rows. */
  banners?: React.ReactNode;
  skeletonRows?: number;
  /**
   * Where to render the "Clear Filters" pill. Pass a node on a line that
   * already exists so applying a filter doesn't shift the table.
   */
  clearFiltersSlot?: HTMLElement | null;
  /**
   * Remember this listing's filters under this key — across tab switches,
   * detail round trips and reloads — instead of losing them when the table
   * unmounts. Omitted means filters are per-mount, as they were.
   *
   * Opt-in because the table serves five listings and only History asked for
   * it. Assumed stable for the table's lifetime: the History keys are built
   * from the scope and the tab, both of which come from the path, and a path
   * change remounts the tab.
   */
  persistKey?: string;
}

interface DateRange {
  start: Date | null;
  end: Date | null;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const RecordTable = <T,>({
  columns,
  rows,
  rowKey,
  loading = false,
  onRowClick,
  accent,
  reviewTone,
  emptyMessage = "No records found",
  filteredEmptyMessage = "No records match these filters",
  banners,
  skeletonRows = 8,
  clearFiltersSlot,
  persistKey,
}: RecordTableProps<T>) => {
  const theme: Theme = useTheme();
  // Seeded once from storage, so a remounted listing comes back filtered as
  // the user left it. `readRecordFilters` prunes anything belonging to a
  // column this table no longer declares.
  const [restored] = useState(() =>
    readRecordFilters(
      persistKey,
      columns.map((column) => column.key)
    )
  );
  const [valueFilters, setValueFilters] = useState<Record<string, string[]>>(
    restored.valueFilters
  );
  const [dateFilters, setDateFilters] = useState<Record<string, DateRange>>(
    restored.dateFilters
  );
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
  const [renderLimit, setRenderLimit] = useState(RENDER_CHUNK);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Mirror every change back. A no-op without a `persistKey`.
  useEffect(() => {
    writeRecordFilters(persistKey, { valueFilters, dateFilters });
  }, [persistKey, valueFilters, dateFilters]);

  /**
   * Drop selections belonging to columns that are no longer declared.
   *
   * Most listings keep a fixed column set, but the Lab Builder's two tabs do
   * not — Status exists only on Custom, since provider catalog panels carry no
   * lab status. A filter left behind on the other tab is invisible yet still
   * counted by the "Clear Filters" pill, and would silently filter rows if a
   * key happened to match. Same reasoning as `readRecordFilters`' pruning,
   * applied while the table is mounted.
   */
  const columnKeys = columns.map((column) => column.key).join("|");
  useEffect(() => {
    const known = new Set(columnKeys.split("|"));
    const prune = <V,>(current: Record<string, V>) => {
      const kept = Object.keys(current).filter((key) => known.has(key));
      if (kept.length === Object.keys(current).length) return current;
      const next: Record<string, V> = {};
      kept.forEach((key) => {
        next[key] = current[key];
      });
      return next;
    };
    setValueFilters(prune);
    setDateFilters(prune);
  }, [columnKeys]);

  const filterTypeOf = (column: RecordColumn<T>) => column.filter ?? "multi";

  /** Every filter key this row carries for this column. */
  const keysOf = (column: RecordColumn<T>, row: T): string[] => {
    if (column.optionsOf) return column.optionsOf(row).map((o) => o.key);
    const key = column.optionKey ? column.optionKey(row) : column.value(row);
    return key ? [key] : [];
  };

  const optionsByColumn = useMemo(() => {
    const result: Record<string, ColumnFilterOption[]> = {};
    columns.forEach((column) => {
      if (filterTypeOf(column) !== "multi") return;
      const map = new Map<string, ColumnFilterOption>();
      rows.forEach((row) => {
        const pairs = column.optionsOf
          ? column.optionsOf(row)
          : (() => {
              const label = column.value(row);
              const key = column.optionKey ? column.optionKey(row) : label;
              return key && label ? [{ key, label }] : [];
            })();
        pairs.forEach(({ key, label }) => {
          if (!key || !label || map.has(key)) return;
          map.set(key, {
            key,
            label,
            initials: column.withInitials ? initialsOf(label) : undefined,
          });
        });
      });
      result[column.key] = Array.from(map.values()).sort((a, b) =>
        a.label.localeCompare(b.label)
      );
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, rows]);

  const activeFilterCount =
    Object.values(valueFilters).filter((selected) => selected.length > 0)
      .length +
    Object.values(dateFilters).filter((range) => range.start !== null).length;

  const clearAllFilters = () => {
    setValueFilters({});
    setDateFilters({});
    // Forget them too, or the next mount restores what was just cleared. Only
    // this listing's key, so clearing one tab leaves the others filtered.
    clearRecordFilters(persistKey);
  };

  const filteredRows = useMemo(
    () =>
      rows.filter((row) =>
        columns.every((column) => {
          const type = filterTypeOf(column);
          if (type === "multi") {
            const selected = valueFilters[column.key];
            if (!selected || selected.length === 0) return true;
            return keysOf(column, row).some((key) => selected.includes(key));
          }
          if (type === "date") {
            const range = dateFilters[column.key];
            if (!range?.start) return true;
            const raw = column.date ? column.date(row) : column.value(row);
            if (!raw) return false;
            const value = raw instanceof Date ? raw : new Date(raw);
            if (isNaN(value.getTime())) return false;
            // A single picked day means that whole day, matching how the
            // toolbar date filter behaves.
            return isWithinInterval(value, {
              start: startOfDay(range.start),
              end: endOfDay(range.end ?? range.start),
            });
          }
          return true;
        })
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, columns, valueFilters, dateFilters]
  );

  // Incremental rendering — see the header comment.
  const visibleRows = useMemo(
    () => filteredRows.slice(0, renderLimit),
    [filteredRows, renderLimit]
  );
  const hiddenCount = filteredRows.length - visibleRows.length;
  const filteredCountRef = useRef(filteredRows.length);
  filteredCountRef.current = filteredRows.length;

  // A new filter is a new list: start from the top again. Both are state
  // objects replaced only when a filter changes, so they are the dependency.
  useEffect(() => {
    setRenderLimit(RENDER_CHUNK);
    scrollRef.current?.scrollTo?.({ top: 0 });
  }, [valueFilters, dateFilters]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollHeight <= 0) return;
      const seen = (el.scrollTop + el.clientHeight) / el.scrollHeight;
      if (seen < RENDER_MORE_THRESHOLD) return;
      setRenderLimit((limit) =>
        limit < filteredCountRef.current ? limit + RENDER_CHUNK : limit
      );
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // A chunk that doesn't fill the scroll area can't be scrolled past the
  // threshold, so keep appending until it overflows or nothing is left.
  useEffect(() => {
    const el = scrollRef.current;
    // A container that hasn't been laid out yet (0 height) would look
    // "not overflowing" on every pass and mount the whole list chunk by chunk.
    if (!el || hiddenCount <= 0 || el.clientHeight <= 0) return;
    if (el.scrollHeight <= el.clientHeight + 1) {
      setRenderLimit((limit) => limit + RENDER_CHUNK);
    }
  }, [visibleRows.length, hiddenCount]);

  const clearFiltersPill =
    activeFilterCount > 0 ? (
      <button
        type="button"
        style={styles.clearFiltersPill(theme)}
        onClick={clearAllFilters}
      >
        <span>Clear Filters</span>
        <span style={styles.clearFiltersCount(theme)}>{activeFilterCount}</span>
      </button>
    ) : null;

  const renderHeaderCell = (column: RecordColumn<T>) => {
    const type = filterTypeOf(column);
    if (type === "date") {
      const range = dateFilters[column.key] ?? { start: null, end: null };
      return (
        <ColumnDateFilterMenu
          title={column.label}
          startDate={range.start}
          endDate={range.end}
          onDateChange={(start, end) =>
            setDateFilters((current) => ({
              ...current,
              [column.key]: { start, end },
            }))
          }
        />
      );
    }
    if (type === "multi") {
      return (
        <ColumnFilterMenu
          title={column.label}
          options={optionsByColumn[column.key] ?? []}
          selectedKeys={valueFilters[column.key] ?? []}
          onSelectionChange={(selected) =>
            setValueFilters((current) => ({ ...current, [column.key]: selected }))
          }
          searchable={column.searchable}
        />
      );
    }
    return <>{column.label}</>;
  };

  return (
    <div style={styles.container}>
      {clearFiltersPill &&
        (clearFiltersSlot ? (
          createPortal(clearFiltersPill, clearFiltersSlot)
        ) : (
          <div style={styles.inlineToolbar}>{clearFiltersPill}</div>
        ))}

      <div style={styles.tableHeader(theme)}>
        {columns.map((column) => (
          <span key={column.key} style={{ flex: column.flex, minWidth: 0 }}>
            {renderHeaderCell(column)}
          </span>
        ))}
      </div>

      <ScrollableWrapper ref={scrollRef}>
        <div style={styles.tableBody}>
          {banners}
          {loading ? (
            <RecordRowsSkeleton count={skeletonRows} />
          ) : filteredRows.length === 0 ? (
            <div style={styles.emptyMessage(theme)}>
              {activeFilterCount > 0 ? filteredEmptyMessage : emptyMessage}
            </div>
          ) : (
            visibleRows.map((row) => {
              const key = rowKey(row);
              const rowAccent = accent ? accent(row) : null;
              const tone = reviewTone ? reviewTone(row) : null;
              const isHovered = hoveredRowKey === key;
              return (
                <div
                  key={key}
                  style={styles.tableRow(theme, rowAccent)}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onMouseEnter={() => setHoveredRowKey(key)}
                  onMouseLeave={() =>
                    setHoveredRowKey((current) =>
                      current === key ? null : current
                    )
                  }
                >
                  {/* Full-height "Review" tile, parked off the left edge and
                      sliding in on hover for rows needing attention. */}
                  {tone && (
                    <div style={styles.reviewTile(theme, tone, isHovered)}>
                      {"Review"}
                    </div>
                  )}
                  {columns.map((column, index) => {
                    const content = column.render
                      ? column.render(row)
                      : column.value(row) || "—";
                    const colStyle = { flex: column.flex, minWidth: 0 };
                    // The first cell hosts the absolutely-positioned Review
                    // tile, so it needs the relative wrapper.
                    if (index === 0) {
                      return (
                        <div key={column.key} style={styles.firstCell(colStyle)}>
                          <span style={styles.cellText(theme, {})}>
                            {content}
                          </span>
                        </div>
                      );
                    }
                    const asNode =
                      column.render && (column.cellStyle ?? "node") === "node";
                    return asNode ? (
                      <div key={column.key} style={{ ...colStyle, display: "flex" }}>
                        {content}
                      </div>
                    ) : (
                      <span
                        key={column.key}
                        style={styles.cellText(theme, colStyle)}
                        // Text cells truncate with an ellipsis; the tooltip
                        // gives the full value back (long lab panel names).
                        title={
                          typeof content === "string" ? content : undefined
                        }
                      >
                        {content}
                      </span>
                    );
                  })}
                </div>
              );
            })
          )}
          {!loading && hiddenCount > 0 && (
            <div style={styles.moreCaption(theme)}>
              {`Showing ${visibleRows.length.toLocaleString()} of ${filteredRows.length.toLocaleString()} — scroll for more`}
            </div>
          )}
        </div>
      </ScrollableWrapper>
    </div>
  );
};

export default RecordTable;

// Rows-only skeleton: the header and its filter menus are already on screen, so
// re-skeletoning them would flash on every filter change.
const RecordRowsSkeleton: React.FC<{ count: number }> = ({ count }) => {
  const theme: Theme = useTheme();
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          sx={{
            width: "100%",
            height: "45px",
            borderRadius: "8px",
            backgroundColor: theme.colorTokens.N[30],
          }}
        />
      ))}
    </>
  );
};

const styles = {
  // Shared with the Orders detail tables so the row card, header type scale,
  // accent and hover treatment can't drift.
  ...ordersTableStyles,
  inlineToolbar: {
    display: "flex",
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  clearFiltersPill: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 8px 6px 12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.PRIMARY_ICON_MAIN}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    cursor: "pointer",
    outline: "none",
    whiteSpace: "nowrap" as "nowrap",
  }),
  moreCaption: (theme: Theme) => ({
    textAlign: "center" as "center",
    padding: "12px 0",
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  clearFiltersCount: (theme: Theme) => ({
    minWidth: "18px",
    height: "18px",
    padding: "0 4px",
    borderRadius: "9px",
    backgroundColor: theme.colors_ds.PRIMARY_ICON_MAIN,
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    fontFamily: "var(--font-Bold)",
    fontSize: "11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }),
};
